// imageCompressor.js — Smart client-side image compression for fast, reliable uploads

/**
 * Compresses an image file before upload to avoid HTTP 413 (Request Entity Too Large).
 * Keeps dimensions up to 1400px with high visual clarity.
 * @param {File} file - Original user image file
 * @param {number} maxDimension - Maximum width/height (default 1400)
 * @param {number} quality - JPEG compression quality 0.1 to 1.0 (default 0.82)
 * @returns {Promise<File>} Compressed File object
 */
export async function compressImage(file, maxDimension = 1400, quality = 0.82) {
  // If file is already small (e.g. < 400 KB) and not a huge PNG, skip compression
  if (!file || !file.type.startsWith("image/") || file.size < 400 * 1024) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Scale down if larger than maxDimension
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG for optimal file size (typically 150KB–300KB)
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file); // Fallback to original
              return;
            }

            const cleanName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
            const compressedFile = new File([blob], cleanName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = () => resolve(file);
    };

    reader.onerror = () => resolve(file);
  });
}
